/*
 * /complete-profile?t=… — the "complete your profile" email landing (design
 * MY-DESIGN-BRAND-SIGNUP-AND-COMPLETE-PROFILE-1020, PR-C). Behind
 * VITE_PROFILE_LINKS_ENABLED; mounted inside the authed shell.
 *
 * SIGN-IN FIRST, exactly as /join/confirm: a signed-out visitor never reaches
 * this component — the auth gate renders Login AT this path (its returnTo
 * brings them back) and App.tsx stashes the token (storePendingProfileLink)
 * for the fresh-tab sign-in link.
 *
 * GET /me/profile-links/{token} then renders:
 *   1. the brand header
 *   2. "Get {brand} news" — "You're subscribed ✓", or one Confirm button
 *      (POST …/subscribe with the read's wording_version; optimistic, reverts
 *      on error; 403 minor_parent_managed ⇒ "a parent or guardian manages
 *      this")
 *   3. "Help us tailor events" — ONLY the missing items: home city (the
 *      wizard step-3 hub edit, as the My newsletters TailorCard), then each
 *      org field (single_choice ⇒ select, multi_choice ⇒ checkboxes, Save
 *      each; 409 ⇒ "Already saved"). Hidden when nothing is missing.
 *   4. a link to My newsletters
 * Errors: 410 ⇒ expired (+ My newsletters link); 403 wrong_account ⇒ sign in
 * with the right address (sign out + retry, token kept); 404 (my-api flag
 * off) ⇒ the catch-all's /get-ready; anything else ⇒ retry.
 * The token is never rendered or logged.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { clearSession, loadSession } from '../auth/session'
import { Card } from '../components/ui'
import {
  fetchProfileLink,
  myApiBase,
  MyApiError,
  saveProfileLinkOrgField,
  storePendingProfileLink,
  subscribeViaProfileLink,
  takePendingProfileLink,
  type ProfileLinkOrgField,
  type ProfileLinkRead,
} from '../data/myApi'
import { useLocale, type Lang, type TranslateFn } from '../i18n/useLocale'
import { NEWSLETTERS_PATH } from '../components/MyNewsletters'
import { NEAREST_CITY_EDIT_PATH } from '../components/NearestCityPrompt'

type Phase = 'loading' | 'ready' | 'missing' | 'expired' | 'wrongAccount' | 'notFound' | 'error'

const headingStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em', fontWeight: 400,
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: 0,
}
const metaStyle: CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45, margin: 0 }
const linkStyle: CSSProperties = { fontWeight: 700, fontSize: 13, color: 'var(--tru-status-teal-bright)' }
const primaryBtn: CSSProperties = {
  minHeight: 40, borderRadius: 9, padding: '0 16px', border: 0, background: 'var(--accent)', color: 'var(--accent-ink)',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 800, cursor: 'pointer', alignSelf: 'flex-start',
}
const outlineBtn: CSSProperties = {
  minHeight: 44, borderRadius: 'var(--r-btn)', border: '1px solid var(--hairline-strong)', background: 'transparent',
  color: 'var(--text)', fontFamily: 'inherit', fontWeight: 800, fontSize: 13, cursor: 'pointer', padding: '0 18px',
}

/** Locale label when the definition carries one, else the default label. */
function localized(label: string, i18n: Record<string, string> | undefined, lang: Lang): string {
  return i18n?.[lang] ?? label
}

/** 410/403 from any of the three calls flips the whole page. */
function pagePhaseFor(e: unknown): Phase | null {
  if (!(e instanceof MyApiError)) return null
  if (e.status === 410 || e.code === 'link_expired') return 'expired'
  if (e.code === 'wrong_account') return 'wrongAccount'
  return null
}

export function CompleteProfile({ fetchImpl }: { fetchImpl?: typeof fetch }) {
  const { t, lang } = useLocale()
  const { search } = useLocation()
  const [token] = useState(() => {
    // The authed app is here now: drop the stash (one-shot).
    takePendingProfileLink()
    return (new URLSearchParams(search).get('t') ?? '').trim()
  })
  const [phase, setPhase] = useState<Phase>(token ? 'loading' : 'missing')
  const [link, setLink] = useState<ProfileLinkRead | null>(null)
  const [attempt, setAttempt] = useState(0)
  const fired = useRef(-1)

  useEffect(() => {
    if (!token || fired.current === attempt) return
    fired.current = attempt
    const session = loadSession()
    const base = myApiBase()
    if (!session || !base) {
      setPhase('error')
      return
    }
    setPhase('loading')
    fetchProfileLink(session.idToken, base, token, fetchImpl ?? fetch)
      .then((r) => {
        setLink(r)
        setPhase('ready')
      })
      .catch((e) => {
        const p = pagePhaseFor(e)
        if (p) setPhase(p)
        else if (e instanceof MyApiError && e.status === 403) setPhase('wrongAccount')
        else if (e instanceof MyApiError && e.status === 404) setPhase('notFound')
        else setPhase('error')
      })
  }, [token, attempt, fetchImpl])

  const switchAccount = () => {
    // Keep the link for the next sign-in, then sign out: the auth gate shows
    // Login at this same path and returns here.
    storePendingProfileLink(token)
    clearSession()
    window.location.reload()
  }

  // my-api flag off ⇒ the same as an unknown path (the shell's catch-all).
  if (phase === 'notFound') return <Navigate to="/get-ready" replace />

  const brand = link?.brand_name.trim() || t('completeProfile.brandFallback')

  return (
    <div data-testid="complete-profile" style={{ maxWidth: 480, margin: '24px auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {phase === 'ready' && link ? (
        <>
          <h1 data-testid="complete-profile-brand" style={{ fontSize: 20, margin: '0 2px' }}>{brand}</h1>
          <NewsCard link={link} brand={brand} token={token} fetchImpl={fetchImpl} t={t} onPagePhase={setPhase} />
          <TailorSection link={link} brand={brand} token={token} fetchImpl={fetchImpl} t={t} lang={lang} onPagePhase={setPhase} />
          <Link data-testid="complete-profile-newsletters" to={NEWSLETTERS_PATH} style={{ ...linkStyle, margin: '0 2px' }}>
            {t('completeProfile.newslettersLink')}
          </Link>
        </>
      ) : (
        <Card>
          <div className="col" style={{ padding: 20, gap: 12, textAlign: 'center' }}>
            {phase === 'loading' && <p role="status" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('completeProfile.loading')}</p>}
            {(phase === 'expired' || phase === 'missing') && (
              <>
                <p data-testid={`complete-profile-${phase}`} role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>
                  {t(phase === 'expired' ? 'completeProfile.expired' : 'completeProfile.missing')}
                </p>
                <Link data-testid="complete-profile-expired-newsletters" to={NEWSLETTERS_PATH} style={linkStyle}>
                  {t('completeProfile.expiredLink')}
                </Link>
              </>
            )}
            {phase === 'wrongAccount' && (
              <>
                <p data-testid="complete-profile-wrong-account" role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('completeProfile.wrongAccount')}</p>
                <button type="button" data-testid="complete-profile-switch" onClick={switchAccount} style={outlineBtn}>{t('completeProfile.switchAccount')}</button>
              </>
            )}
            {phase === 'error' && (
              <>
                <p data-testid="complete-profile-error" role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('completeProfile.error')}</p>
                <button type="button" data-testid="complete-profile-retry" onClick={() => setAttempt((a) => a + 1)} style={outlineBtn}>{t('completeProfile.retry')}</button>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}

interface SectionProps {
  link: ProfileLinkRead
  brand: string
  token: string
  fetchImpl?: typeof fetch
  t: TranslateFn
  onPagePhase: (p: Phase) => void
}

function NewsCard({ link, brand, token, fetchImpl, t, onPagePhase }: SectionProps) {
  const [subscribed, setSubscribed] = useState(link.subscribed === 'subscribed')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<'error' | 'minor' | null>(null)

  const confirm = () => {
    if (busy || subscribed) return
    const session = loadSession()
    const base = myApiBase()
    if (!session || !base) {
      setError('error')
      return
    }
    // Optimistic: show subscribed now; revert on error.
    setSubscribed(true)
    setBusy(true)
    setError(null)
    subscribeViaProfileLink(session.idToken, base, token, link.wording_version, fetchImpl ?? fetch)
      .catch((e) => {
        setSubscribed(false)
        if (e instanceof MyApiError && e.code === 'minor_parent_managed') {
          setError('minor')
          return
        }
        const p = pagePhaseFor(e)
        if (p) onPagePhase(p)
        else setError('error')
      })
      .finally(() => setBusy(false))
  }

  return (
    <Card>
      <div data-testid="complete-profile-news" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{t('completeProfile.news.title', { brand })}</p>
        <p style={metaStyle}>{t('completeProfile.news.body', { brand })}</p>
        {subscribed ? (
          <p data-testid="complete-profile-subscribed" role="status" style={{ margin: 0, fontWeight: 700, fontSize: 13, color: 'var(--tru-status-teal-bright)' }}>
            {t('completeProfile.news.subscribed')}
          </p>
        ) : error !== 'minor' ? (
          <button type="button" data-testid="complete-profile-subscribe" onClick={confirm} disabled={busy} style={primaryBtn}>
            {t('completeProfile.news.confirm')}
          </button>
        ) : null}
        {error && (
          <p data-testid={error === 'minor' ? 'complete-profile-minor' : 'complete-profile-news-error'} role="alert" style={{ ...metaStyle, color: error === 'minor' ? 'var(--text-secondary)' : 'var(--warn)' }}>
            {t(error === 'minor' ? 'completeProfile.news.minor' : 'completeProfile.news.error')}
          </p>
        )}
      </div>
    </Card>
  )
}

function TailorSection({ link, brand, token, fetchImpl, t, lang, onPagePhase }: SectionProps & { lang: Lang }) {
  const navigate = useNavigate()
  const { city, org_fields } = link.missing
  if (!city && org_fields.length === 0) return null
  return (
    <Card>
      <div data-testid="complete-profile-tailor" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={headingStyle}>{t('tailor.kicker')}</p>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{t('completeProfile.tailor.title')}</p>
          <p style={metaStyle}>{t('completeProfile.tailor.body', { brand })}</p>
        </div>
        {city && (
          <button type="button" data-testid="complete-profile-add-city" onClick={() => navigate(NEAREST_CITY_EDIT_PATH)} style={primaryBtn}>
            {t('completeProfile.tailor.addCity')}
          </button>
        )}
        {org_fields.map((f) => (
          <OrgFieldRow key={f.definition_id} field={f} token={token} fetchImpl={fetchImpl} t={t} lang={lang} onPagePhase={onPagePhase} />
        ))}
      </div>
    </Card>
  )
}

type SaveState = 'idle' | 'saving' | 'saved' | 'already' | 'error'

function OrgFieldRow({ field, token, fetchImpl, t, lang, onPagePhase }: {
  field: ProfileLinkOrgField
  token: string
  fetchImpl?: typeof fetch
  t: TranslateFn
  lang: Lang
  onPagePhase: (p: Phase) => void
}) {
  const [single, setSingle] = useState('')
  const [multi, setMulti] = useState<string[]>([])
  const [state, setState] = useState<SaveState>('idle')
  const label = localized(field.label, field.label_i18n, lang)
  const inputId = `cp-field-${field.definition_id}`
  const done = state === 'saved' || state === 'already'
  const hasValue = field.type === 'single_choice' ? single !== '' : multi.length > 0

  const save = () => {
    if (!hasValue || state === 'saving' || done) return
    const session = loadSession()
    const base = myApiBase()
    if (!session || !base) {
      setState('error')
      return
    }
    setState('saving')
    const value = field.type === 'single_choice' ? single : multi
    saveProfileLinkOrgField(session.idToken, base, token, field.definition_id, value, fetchImpl ?? fetch)
      .then(() => setState('saved'))
      .catch((e) => {
        if (e instanceof MyApiError && (e.status === 409 || e.code === 'already_answered')) {
          setState('already')
          return
        }
        const p = pagePhaseFor(e)
        if (p) onPagePhase(p)
        else setState('error')
      })
  }

  const toggle = (v: string) => setMulti((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]))

  return (
    <div data-testid="complete-profile-field" data-definition-id={field.definition_id} style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid var(--hairline-06)', paddingTop: 10 }}>
      {field.type === 'single_choice' ? (
        <>
          <label htmlFor={inputId} style={{ fontSize: 13, fontWeight: 700 }}>{label}</label>
          <select
            id={inputId}
            data-testid="complete-profile-field-select"
            value={single}
            disabled={done}
            onChange={(e) => setSingle(e.target.value)}
            style={{ minHeight: 40, borderRadius: 9, border: '1px solid var(--hairline-12)', background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, padding: '0 10px' }}
          >
            <option value="">{t('completeProfile.field.choose')}</option>
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>{localized(o.label, o.label_i18n, lang)}</option>
            ))}
          </select>
        </>
      ) : (
        <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <legend style={{ fontSize: 13, fontWeight: 700, padding: 0, marginBottom: 4 }}>{label}</legend>
          {field.options.map((o) => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                data-testid="complete-profile-field-checkbox"
                value={o.value}
                checked={multi.includes(o.value)}
                disabled={done}
                onChange={() => toggle(o.value)}
              />
              {localized(o.label, o.label_i18n, lang)}
            </label>
          ))}
        </fieldset>
      )}
      {done ? (
        <p data-testid="complete-profile-field-status" role="status" style={{ ...metaStyle, fontWeight: 700, color: 'var(--tru-status-teal-bright)' }}>
          {t(state === 'saved' ? 'completeProfile.field.saved' : 'completeProfile.field.alreadySaved')}
        </p>
      ) : (
        <button type="button" data-testid="complete-profile-field-save" onClick={save} disabled={!hasValue || state === 'saving'} style={{ ...primaryBtn, opacity: hasValue ? 1 : 0.6 }}>
          {t('completeProfile.field.save')}
        </button>
      )}
      {state === 'error' && (
        <p data-testid="complete-profile-field-error" role="alert" style={{ ...metaStyle, color: 'var(--warn)' }}>{t('completeProfile.field.error')}</p>
      )}
    </div>
  )
}
